import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { User, Mail, Phone, Building2, Shield, X, Plus, Lock, Eye, EyeOff } from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const AddEditAgentModal = ({ isOpen, onClose, agent, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    department: '',
    defaultPassword: '',
    role: '',
    status: 'active'
  });
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [countryCode, setCountryCode] = useState('+1');
  const [showPassword, setShowPassword] = useState(false);

  // Hardcoded roles as per user requirement
  const roles = [
    { value: 'admin', label: 'Admin' },
    { value: 'agent', label: 'Agent' },
    { value: 'enduser', label: 'End User' },
    { value: 'teamleader', label: 'Team Leader' }
  ];

  useEffect(() => {
    if (isOpen) {
      loadFormData();
    }
  }, [isOpen, agent]);

  const loadFormData = async () => {
    setLoading(true);
    try {
      // Load departments
      const departmentsRes = await MockDataService.getDepartments();

      if (departmentsRes.success) {
        setDepartments(departmentsRes.data || []);
      }

      // Populate form if editing
      if (agent) {
        const phoneNumber = agent.phone || agent.availability?.phone || '';
        // Extract country code from phone number if it exists
        let extractedCountryCode = '+1';
        let phoneWithoutCode = phoneNumber;
        if (phoneNumber) {
          const codeMatch = phoneNumber.match(/^(\+\d{1,4})\s*/);
          if (codeMatch) {
            extractedCountryCode = codeMatch[1];
            phoneWithoutCode = phoneNumber.replace(/^\+\d{1,4}\s*/, '');
          }
        }
        
        setCountryCode(extractedCountryCode);
        setFormData({
          name: agent.name || '',
          email: agent.email || '',
          phone: phoneWithoutCode,
          department: agent.department || '',
          defaultPassword: '', // Don't show existing password
          role: agent.role || '',
          status: agent.status || 'active'
        });
      } else {
        // Reset form for new agent
        setCountryCode('+1');
        setFormData({
          name: '',
          email: '',
          phone: '',
          department: '',
          defaultPassword: '',
          role: '',
          status: 'active'
        });
      }
      setShowPassword(false);
      setErrors({});
    } catch (error) {
      console.error('Error loading form data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    // Department is optional for all roles
    // No validation needed
    
    if (!formData.defaultPassword.trim()) {
      newErrors.defaultPassword = 'Default password is required';
    } else if (formData.defaultPassword.length < 8) {
      newErrors.defaultPassword = 'Password must be at least 8 characters long';
    }
    
    if (!formData.role) {
      newErrors.role = 'Role is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const agentData = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim() ? `${countryCode} ${formData.phone.trim()}` : '',
      department: formData.department,
      defaultPassword: formData.defaultPassword.trim(),
      role: formData.role,
      status: formData.status,
      availability: {
        status: formData.status === 'active' ? 'online' : 'offline'
      }
    };

    onSave(agentData);
  };

  const countryCodes = [
    { value: '+1', label: '+1 (US/Canada)' },
    { value: '+44', label: '+44 (UK)' },
    { value: '+91', label: '+91 (India)' },
    { value: '+61', label: '+61 (Australia)' },
    { value: '+49', label: '+49 (Germany)' },
    { value: '+33', label: '+33 (France)' },
    { value: '+81', label: '+81 (Japan)' },
    { value: '+86', label: '+86 (China)' },
    { value: '+971', label: '+971 (UAE)' },
    { value: '+65', label: '+65 (Singapore)' }
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={agent ? 'Edit Agent' : 'Add New Agent'}
      size="xl"
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="flex flex-col h-full max-h-[calc(90vh-120px)]">
          <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <form onSubmit={handleSubmit} id="agent-form" className="space-y-6">
          {/* Personal Information Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
              <User className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                  Full Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter agent full name"
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="text-sm text-red-500">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email Address <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="agent@company.com"
                    className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-red-500">{errors.email}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                  Phone Number
                </Label>
                <div className="flex gap-2">
                  <div className="w-28">
                    <Select
                      value={countryCode}
                      onValueChange={(value) => setCountryCode(value)}
                    >
                      <SelectTrigger className="h-10 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-[180px] overflow-y-auto z-[60] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                        {countryCodes.map((code) => (
                          <SelectItem key={code.value} value={code.value}>
                            {code.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="(555) 123-4567"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="defaultPassword" className="text-sm font-medium text-gray-700">
                  Default Password <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="defaultPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.defaultPassword}
                    onChange={(e) => handleInputChange('defaultPassword', e.target.value)}
                    placeholder="Enter default password (min 8 characters)"
                    className={`pl-10 pr-10 ${errors.defaultPassword ? 'border-red-500' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.defaultPassword && (
                  <p className="text-sm text-red-500">{errors.defaultPassword}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">Password must be at least 8 characters long</p>
              </div>
            </div>
          </div>

          {/* Work Information Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
              <Building2 className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Work Information</h3>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {/* COMMENTED OUT: Department field hidden per requirement */}
              {/* <div className="space-y-2">
                <Label htmlFor="department" className="text-sm font-medium text-gray-700">
                  Department <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.department}
                  onValueChange={(value) => handleInputChange('department', value)}
                >
                  <SelectTrigger className={errors.department ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id || dept.name} value={dept.id || dept.name}>
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.department && (
                  <p className="text-sm text-red-500">{errors.department}</p>
                )}
              </div> */}

              <div className="space-y-2">
                <Label htmlFor="role" className="text-sm font-medium text-gray-700">
                  Role <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => handleInputChange('role', value)}
                >
                  <SelectTrigger className={errors.role ? 'border-red-500 h-10' : 'h-10'}>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[180px] overflow-y-auto z-[60] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                    {roles.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-gray-500" />
                          {role.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.role && (
                  <p className="text-sm text-red-500">{errors.role}</p>
                )}
              </div>
            </div>
          </div>

            </form>
          </div>
          
          {/* Action Buttons - Fixed at Bottom */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="px-6"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="agent-form"
              className="px-6 bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              {agent ? 'Update Agent' : 'Create'}
            </Button>
          </div>
        </div>
      )}

    </Modal>
  );
};

export default AddEditAgentModal;

